/**
 * Адрес, куда человек шёл до страницы входа.
 *
 * Лежит в localStorage, а не в состоянии истории роутера: по ссылке на скрытую
 * услугу чаще всего приходит родитель, которого в этом браузере ещё нет. Его
 * путь — вход → регистрация → письмо → подтверждение → снова вход, нередко
 * в другой вкладке, и состояние истории на нём теряется в первом же переходе.
 *
 * Срок жизни — сутки: ссылку из письма открывают и вечером того же дня,
 * но вход через неделю не должен уводить на оплату услуги, о которой человек
 * уже забыл. Адрес стирается при чтении и при выходе из кабинета.
 */

const KEY = 'studioapp.returnTo';

const LifetimeMs = 24 * 60 * 60 * 1000;

interface Stored {
  path: string;
  at: number;
}

/**
 * Страница, с которой человек вышел сам.
 *
 * Выход гасит пользователя раньше, чем происходит переход, поэтому защита
 * маршрута успевает увидеть гостя на закрытой странице и запоминает её как
 * цель возврата — хотя человек оттуда уходит, а не идёт туда. Запрет адресный
 * и одноразовый: запоминаем, какую именно страницу покинули, и пропускаем
 * ровно одну попытку её сохранить. Так выход со страницы новостей не съедает
 * цель, если человек сразу после него пойдёт по присланной ссылке.
 */
let abandoned: string | null = null;

export const returnTo = {
  save(path: string): void {
    const abandonedNow = abandoned === path;
    abandoned = null;

    if (abandonedNow || !isInternal(path)) {
      return;
    }

    const stored: Stored = { path, at: Date.now() };
    localStorage.setItem(KEY, JSON.stringify(stored));
  },

  /** Вызывается при выходе: эту страницу запоминать не нужно. */
  forget(path: string): void {
    abandoned = path;
  },

  /**
   * Читает адрес, не стирая его: неудачный ввод пароля не должен терять цель.
   * Стирает её успешный вход — он же её и использует.
   */
  peek(): string | null {
    const raw = localStorage.getItem(KEY);

    if (raw === null) {
      return null;
    }

    const stored = parse(raw);

    if (stored === null || Date.now() - stored.at > LifetimeMs) {
      return null;
    }

    return stored.path;
  },

  clear(): void {
    localStorage.removeItem(KEY);
  },
};

function parse(raw: string): Stored | null {
  // В хранилище могла остаться запись чужого формата — от прошлой версии
  // приложения или подставленная руками.
  try {
    const value: unknown = JSON.parse(raw);

    if (
      typeof value !== 'object' ||
      value === null ||
      !('path' in value) ||
      !('at' in value) ||
      typeof value.path !== 'string' ||
      typeof value.at !== 'number' ||
      !isInternal(value.path)
    ) {
      return null;
    }

    return { path: value.path, at: value.at };
  } catch {
    return null;
  }
}

/**
 * Только адрес внутри приложения. Подставленный в хранилище `//evil.example`
 * роутер понял бы как протокол-относительную ссылку и увёл бы на чужой сайт.
 *
 * Обратная косая черта запрещена по той же причине: в адресе браузер читает
 * её как прямую, и `/\evil.example` превращается в `https://evil.example/`.
 */
function isInternal(path: string): boolean {
  return path.startsWith('/') && !path.startsWith('//') && !path.includes('\\');
}
